'use client';

import { submitRIIPForm } from '@/lib/supabase/form-actions'
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { riipFormSchema, type RIIPFormData } from '@/lib/types/forms';
import { captureAnalyticsEvent } from '@/lib/analytics';
import { buildRIIPWhatsAppMessage, buildWhatsAppUrl } from '@/lib/links';
import { toast } from 'sonner';

interface RIIPFormDialogProps {
  trigger: React.ReactNode;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  dialogTitle?: string;
  submitLabel?: string;
  inquiryType?: 'riip' | 'cap';
}

export function RIIPFormDialog({
  trigger,
  onSuccess,
  onError,
  dialogTitle = 'RIIP Program Inquiry',
  submitLabel = 'Submit',
  inquiryType = 'riip',
}: RIIPFormDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<RIIPFormData>({
    resolver: zodResolver(riipFormSchema),
    defaultValues: {
      name: '',
      designation: '',
      institutionName: '',
      email: '',
      phone: '',
      groupSize: '',
    },
  });

  const onSubmit = async (data: RIIPFormData) => {
    captureAnalyticsEvent('inquiry_submitted', { inquiry_type: inquiryType });
    setIsSubmitting(true);
    try {
      await submitRIIPForm(data, inquiryType);
      captureAnalyticsEvent('inquiry_submission_succeeded', { inquiry_type: inquiryType });
      captureAnalyticsEvent('booking_intent_clicked', { inquiry_type: inquiryType, channel: 'whatsapp' });
      window.open(
        buildWhatsAppUrl(buildRIIPWhatsAppMessage(data, inquiryType)),
        '_blank',
        'noopener,noreferrer',
      );
      toast.success('Opening WhatsApp', {
        description: 'Your enquiry is saved — send the message to confirm with us.',
      });
      setOpen(false);
      form.reset();
      onSuccess?.();
    } catch (error) {
      captureAnalyticsEvent('inquiry_submission_failed', { inquiry_type: inquiryType });
      onError?.(error as Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !open) {
      captureAnalyticsEvent('inquiry_opened', { inquiry_type: inquiryType });
    }
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="designation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Designation *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Teacher, Principal" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="institutionType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Institution Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="school">School</SelectItem>
                      <SelectItem value="college">College</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="institutionName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Institution Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter institution name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="Enter phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <p className="text-xs text-muted-foreground -mt-2">
              * Provide at least one contact method (email or phone)
            </p>

            <FormField
              control={form.control}
              name="groupSize"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Group Size (Optional)</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="Number of participants" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
